import { apiClient } from './api';

/**
 * REV-214: client for the org-scoped remediation-plan endpoints. Plans live
 * inside a client organization; items can span multiple Tracked Places.
 */

export type RemediationPlanStatus = 'draft' | 'active' | 'completed' | 'archived';
export type RemediationItemStatus = 'open' | 'in_progress' | 'done' | 'wont_fix';
export type RemediationPriority = 'critical' | 'high' | 'normal' | 'low';

export interface RemediationPlanSummary {
  id: string;
  name: string;
  description: string | null;
  status: RemediationPlanStatus;
  target_date: string | null;
  created_by_user_id: string;
  createdBy?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
  // The API serializes Sequelize timestamps as camelCase (createdAt/updatedAt),
  // NOT snake_case like the domain columns (target_date). Matching the real wire
  // shape — the previous snake_case fields were always undefined, which rendered
  // "Invalid Date" in the plans table.
  createdAt: string;
  updatedAt: string;
}

export interface RemediationItem {
  id: string;
  plan_id: string;
  title: string;
  description: string | null;
  priority: RemediationPriority;
  status: RemediationItemStatus;
  position: number;
  context_id: string | null;
  finding_instance_id: string | null;
  due_date: string | null;
  created_by_user_id: string;
  context?: {
    id: string;
    name: string;
    canonical_identifier: string;
  } | null;
  finding?: {
    id: string;
    finding_definition_id: string;
    finding_type: string;
    status: string;
    severity_override: string | null;
    target_id: string;
  } | null;
  createdBy?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface RemediationPlanDetail extends RemediationPlanSummary {
  items: RemediationItem[];
}

export interface CreatePlanInput {
  name: string;
  description?: string | null;
  status?: RemediationPlanStatus;
  target_date?: string | null;
}

export type UpdatePlanInput = Partial<CreatePlanInput>;

export interface CreateItemInput {
  title: string;
  description?: string | null;
  priority?: RemediationPriority;
  status?: RemediationItemStatus;
  position?: number;
  context_id?: string | null;
  finding_instance_id?: string | null;
  due_date?: string | null;
}

export type UpdateItemInput = Partial<CreateItemInput>;

const base = (orgId: string) => `/v1/organizations/${orgId}/remediation-plans`;

export const remediationService = {
  async listPlans(
    orgId: string,
    filters: { status?: RemediationPlanStatus } = {}
  ): Promise<RemediationPlanSummary[]> {
    const res = await apiClient.get<{ success: boolean; data: RemediationPlanSummary[] }>(
      base(orgId),
      filters.status ? { status: filters.status } : undefined
    );
    return res.data;
  },

  async getPlan(orgId: string, planId: string): Promise<RemediationPlanDetail> {
    const res = await apiClient.get<{ success: boolean; data: RemediationPlanDetail }>(
      `${base(orgId)}/${planId}`
    );
    return res.data;
  },

  async createPlan(orgId: string, input: CreatePlanInput): Promise<RemediationPlanSummary> {
    const res = await apiClient.post<{ success: boolean; data: RemediationPlanSummary }>(
      base(orgId),
      input
    );
    return res.data;
  },

  async updatePlan(
    orgId: string,
    planId: string,
    input: UpdatePlanInput
  ): Promise<RemediationPlanSummary> {
    const res = await apiClient.patch<{ success: boolean; data: RemediationPlanSummary }>(
      `${base(orgId)}/${planId}`,
      input
    );
    return res.data;
  },

  async removePlan(orgId: string, planId: string): Promise<void> {
    await apiClient.delete(`${base(orgId)}/${planId}`);
  },

  async createItem(
    orgId: string,
    planId: string,
    input: CreateItemInput
  ): Promise<RemediationItem> {
    const res = await apiClient.post<{ success: boolean; data: RemediationItem }>(
      `${base(orgId)}/${planId}/items`,
      input
    );
    return res.data;
  },

  async updateItem(
    orgId: string,
    planId: string,
    itemId: string,
    input: UpdateItemInput
  ): Promise<RemediationItem> {
    const res = await apiClient.patch<{ success: boolean; data: RemediationItem }>(
      `${base(orgId)}/${planId}/items/${itemId}`,
      input
    );
    return res.data;
  },

  async removeItem(orgId: string, planId: string, itemId: string): Promise<void> {
    await apiClient.delete(`${base(orgId)}/${planId}/items/${itemId}`);
  },
};
